import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import map from 'lodash/map';
import { createSelector } from 'reselect';
import { nullIfFirstArgNegative } from '@Utils';
import { Licenses } from '@Constants/CommonConstants';
import { license as licenseSelector } from '@Containers/UserDetails/UserDetailsSelectors';
import { context as contextSelector } from '@Selectors/StateParamsSelectors';
import { isReaxysContent as isReaxysContentSelector } from '@Services/UserDatabases/UserDatabasesSelectors';
import { validateHeatmapDataImprint } from '@Pages/Heatmap/HeatmapDataImprint';
import { REDUCER_KEY_HEATMAP } from './constants';
import { initialState } from './reducer';

function remapSelectionIndexes(indexes) {
  return map(indexes, (index) => parseInt(index, 10) + 1);
}

export function heatmapState(state) {
  return state.get(REDUCER_KEY_HEATMAP);
}

export function open(state) {
  return heatmapState(state).open;
}

export function settings(state) {
  return heatmapState(state).settings;
}

export function fullScreenMode(state) {
  const _settings = settings(state);
  return _settings && _settings.startInFullScreen;
}

export function cellOperation(state) {
  const _settings = settings(state);
  return _settings && _settings.cellOperation;
}

function notAReactionContext(context) {
  return context !== 'reactions';
}

export function heatmapLicenseCheck(license) {
  return license === Licenses.ReaxysMedChem || license === Licenses.Both;
}

export const isHeatmapAvailable = createSelector(
  [licenseSelector, contextSelector, isReaxysContentSelector],
  (license, context, isReaxysContent) => isReaxysContent && heatmapLicenseCheck(license) && notAReactionContext(context)
);

export const paramsFromProps = (state, ownProps) => ownProps.params;

export const heatmapDataImprint = (state) => heatmapState(state).heatmapDataImprint;

export function heatmapData(state) {
  return heatmapState(state).heatmapData;
}

export const heatmapDataForParams = createSelector(
  [heatmapData, heatmapDataImprint, paramsFromProps, settings],
  (_heatmapData, _heatmapDataImprint, _params, _settings) =>
    validateHeatmapDataImprint(_heatmapDataImprint, _params, _settings) ? _heatmapData : initialState.heatmapData
);

export function matrixReceived(state) {
  return heatmapState(state).matrixReceived;
}

export const matrixReceivedForParams = createSelector(
  [matrixReceived, heatmapDataImprint, paramsFromProps, settings],
  (_matrixReceived, _heatmapDataImprint, _params, _settings) =>
    validateHeatmapDataImprint(_heatmapDataImprint, _params, _settings) ? _matrixReceived : initialState.matrixReceived
);

export function selectionInfo(state) {
  return heatmapState(state).selectionInfo;
}

export const selectionInfoMappedForRequest = createSelector(
  [selectionInfo],
  nullIfFirstArgNegative((_selectionInfo) => ({
    ...(!isEmpty(_selectionInfo.selectedRows) && {
      rows: {
        indexes: remapSelectionIndexes(_selectionInfo.selectedRows),
      },
    }),
    ...(!isEmpty(_selectionInfo.selectedColumns) && {
      cols: {
        indexes: remapSelectionIndexes(_selectionInfo.selectedColumns),
      },
    }),
  }))
);

export function isLimitDisabled(state) {
  const _selectionInfo = selectionInfo(state);

  if (!_selectionInfo) {
    return true;
  }

  const selectedRows = get(_selectionInfo, 'selectedRows', []);
  const selectedColumns = get(_selectionInfo, 'selectedColumns', []);

  return (
    (isEmpty(selectedRows) && isEmpty(selectedColumns)) ||
    selectedRows.length === _selectionInfo.totalRows ||
    selectedColumns.length === _selectionInfo.totalColumns
  );
}
